import React, { useState, useMemo } from 'react';
import { TranslationFile, SynonymGroup } from '../types';
import { FilePlus, FileText, CheckCircle, Loader2, Zap, ShieldCheck, Download, Trash2, Eye, RefreshCw, BookMarked, X, ArrowRight, HelpCircle } from 'lucide-react';
import Tooltip from './Tooltip';
import { isCommercial } from '../config/appMode';
import TokenBadge from './shared/TokenBadge';
import InlineCostBadge from './commercial/InlineCostBadge';
import { selectPrimaryCredits, sumPrimaryCredits } from '../utils/creditSelector';
import { parseMatchedRulesString } from '../utils/highlightTerms';
import { findMatchingSynonymGroupsForSegments } from '../utils/glossaryUtils';

interface TranslateTabProps {
    files: TranslationFile[];
    /** Project's importedTerminology — dùng compute matched rules trên-the-fly trước khi dịch */
    synonymGroups?: SynonymGroup[];
    onUploadClick: () => void;
    onRunFile: (fileId: string) => void;
    onDeleteFile: (fileId: string) => void;
    onSelectFile: (fileId: string) => void;
    onRunAll?: () => void;
    onToggleFileQC?: (fileId: string, skipQC: boolean) => void;
    onToggleAllQC?: (skipQC: boolean) => void;
    onRunQC?: (fileId: string) => void;
    streamingCount?: number;
    totalSegments?: number;
    activeTranslatingFileId?: string;
}

const TranslateTab: React.FC<TranslateTabProps> = ({
    files,
    synonymGroups = [],
    onUploadClick,
    onRunFile,
    onDeleteFile,
    onSelectFile,
    onRunAll,
    onToggleFileQC,
    onRunQC,
    streamingCount = 0,
    totalSegments = 0,
    activeTranslatingFileId,
}) => {
    const [rulesModalFileId, setRulesModalFileId] = useState<string | null>(null);
    const rulesModalFile = rulesModalFileId ? files.find(f => f.id === rulesModalFileId) : null;

    // Compute matched rules per file on-the-fly từ sourceText + glossary.
    // Hiển thị TRƯỚC KHI dịch (live preview). Sau dịch fallback về stored matchedRules.
    const liveMatchedRules = useMemo(() => {
        const map = new Map<string, { source: string; target: string }[]>();
        for (const f of files) {
            // DOCX backend parser stateless: text ở backendSegments[], không phải sourceText
            const searchText = f.sourceText && f.sourceText.length > 0
                ? f.sourceText
                : (f.backendSegments && f.backendSegments.length > 0)
                    ? f.backendSegments.join('\n')
                    : '';
            if (!searchText || synonymGroups.length === 0) {
                map.set(f.id, []);
                continue;
            }
            const matched = findMatchingSynonymGroupsForSegments(searchText, synonymGroups);
            const excludedSet = new Set((f.excludedRules || []).map((r: string) => r.toLowerCase()));
            const rules = matched
                .filter((m: any) => !excludedSet.has(m.match.toLowerCase()))
                .map((m: any) => ({
                    source: m.match,
                    target: m.group.targets.find((t: any) => t.isDefault)?.text || m.group.targets[0]?.text || ''
                }));
            map.set(f.id, rules);
        }
        return map;
    }, [files, synonymGroups]);

    // Get rules cho 1 file: ưu tiên stored matchedRules (sau dịch) → fallback live compute
    const getRulesForFile = (file: TranslationFile): { source: string; target: string }[] => {
        if (file.matchedRules) return parseMatchedRulesString(file.matchedRules);
        return liveMatchedRules.get(file.id) || [];
    };

    const rulesModalRules = rulesModalFile ? getRulesForFile(rulesModalFile) : [];

    const pendingFiles = files.filter(f =>
        f.translationStatus === 'pending' || f.translationStatus === 'error'
    );
    const pendingCount = pendingFiles.length;
    // Compute per-file credits: translate (always) + qc (if enabled). Sum cho "Dịch tất cả".
    const fileCredits = (f: TranslationFile): number | null => {
        const translate = selectPrimaryCredits(f.estimatedCredits, 'translate');
        if (translate == null) return null;
        const qc = !f.skipQC ? (f.estimatedCredits?.qc ?? 0) : 0;
        return translate + (typeof qc === 'number' ? qc : 0);
    };
    const pendingTokenSum = pendingFiles.reduce((s, f) => {
        const c = fileCredits(f);
        return c == null ? s : s + c;
    }, 0);



    return (
        <div className="max-w-5xl mx-auto space-y-6">
            {/* Intro Banner — compact horizontal (match Train tab pattern) */}
            <div className="p-4 bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 border border-emerald-200 rounded-xl relative overflow-hidden group">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 to-teal-500 opacity-75" />
                <div className="flex items-start gap-4 relative z-10">
                    <div className="w-12 h-12 bg-white/60 backdrop-blur border border-emerald-200 rounded-xl flex items-center justify-center shrink-0 shadow-sm group-hover:scale-105 transition-transform">
                        <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" /></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-emerald-900 text-lg tracking-tight mb-1">Trải nghiệm Dịch thuật tinh tế</h3>
                        <p className="text-sm text-emerald-800/80 leading-relaxed max-w-3xl">
                            Dành trọn tâm trí cho chuyên môn của bạn. Từ <strong className="font-semibold text-emerald-900">giữ nguyên định dạng</strong> đến <strong className="font-semibold text-emerald-900">văn phong mượt mà</strong>, trí công nghệ AI sẽ lo phần mệt nhọc nhất.
                        </p>
                    </div>
                </div>
            </div>

            {/* AI disclaimer — nhắc người dùng review trước khi dùng output */}
            <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" /></svg>
                <span>AI có thể sai sót — luôn rà soát bản dịch trước khi sử dụng.</span>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
                <button
                    onClick={onUploadClick}
                    data-tour="dropzone"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 transition-all shadow-md shadow-emerald-200 hover:shadow-lg active:scale-95"
                >
                    <FilePlus className="w-4 h-4" />
                    Nạp tài liệu cần dịch
                </button>
            </div>

            {/* Production Files Table */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                    <h3 className="font-bold text-slate-700 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-indigo-500" />
                        Hàng đợi dịch
                    </h3>
                    <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-400 font-medium">
                            {files.length} tài liệu
                        </span>
                        {onRunAll && (
                            <button
                                onClick={onRunAll}
                                disabled={pendingCount === 0}
                                data-tour="translate"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-bold rounded-xl hover:bg-emerald-700 transition-colors active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-emerald-600 shadow-sm shadow-emerald-200"
                            >
                                <Zap className="w-4 h-4 fill-current" />
                                Dịch tất cả
                                {pendingCount > 0 && (
                                    <span className="ml-0.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-black bg-white/25 rounded-md tabular-nums">
                                        {pendingCount}
                                    </span>
                                )}
                            </button>
                        )}
                    </div>
                </div>
                <table className="w-full text-left">
                    <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase font-bold tracking-wider">
                        <tr>
                            <th className="px-6 py-3">Tên tài liệu</th>
                            <th className="px-6 py-3 w-32 text-center">
                                <Tooltip content={<div className="bg-slate-900 text-slate-100 text-[11px] leading-snug font-normal normal-case tracking-normal px-2.5 py-1.5 rounded max-w-[200px]">Chi phí ước tính cho file này. Trừ vào số dư khi bấm Dịch.</div>}>
                                    <span className="inline-flex items-center gap-1 cursor-help">
                                        Credit
                                        <HelpCircle className="w-3 h-3 text-slate-400" />
                                    </span>
                                </Tooltip>
                            </th>
                            <th className="px-6 py-3 w-40 text-center">
                                <Tooltip content={<div className="bg-slate-900 text-slate-100 text-[11px] leading-snug font-normal normal-case tracking-normal px-2.5 py-1.5 rounded max-w-[200px]">Bấm tia sét để dịch từng file, hoặc "Dịch tất cả" ở trên.</div>}>
                                    <span className="inline-flex items-center gap-1 cursor-help">
                                        Bản dịch
                                        <HelpCircle className="w-3 h-3 text-slate-400" />
                                    </span>
                                </Tooltip>
                            </th>
                            <th className="px-6 py-3 w-36 text-center whitespace-nowrap">
                                <Tooltip content={<div className="bg-slate-900 text-slate-100 text-[11px] leading-snug font-normal normal-case tracking-normal px-2.5 py-1.5 rounded max-w-[220px]">
                                    Agent Kiểm Tra chấm điểm bản dịch theo 5 tiêu chí. Tick để chạy sau khi dịch (+~20% credit).
                                    <div className="mt-1 pt-1 border-t border-white/15 text-slate-300">Đổi ngôn ngữ <strong className="text-white">Agent Kiểm Tra</strong> trong tab <strong className="text-white">Cấu hình</strong>.</div>
                                </div>}>
                                    <span className="inline-flex items-center gap-1 cursor-help">
                                        Kiểm tra
                                        <HelpCircle className="w-3 h-3 text-slate-400" />
                                    </span>
                                </Tooltip>
                            </th>
                            <th className="px-6 py-4 w-12 text-right"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {files.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-6 py-12 text-center text-slate-400">
                                    <div className="flex flex-col items-center gap-2 text-sm">
                                        {isCommercial ? (
                                            <>
                                                <p className="text-slate-600 font-bold text-lg mb-2">Hàng chờ dịch đang trống</p>
                                                <div className="bg-white px-6 py-4 rounded-xl border border-slate-200 text-left w-full max-w-lg shadow-sm">
                                                    <p className="text-slate-700 font-medium mb-3">Bắt đầu quá trình dịch thuật nhanh chóng:</p>
                                                    <ol className="list-decimal pl-5 text-slate-600 space-y-2 text-sm leading-relaxed">
                                                        <li>Bấm nút <strong className="text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">Nạp tài liệu cần dịch</strong> ở trên.</li>
                                                        <li>Tải lên tài liệu Tiếng Việt của bạn (hỗ trợ .docx, .pptx).</li>
                                                        <li>Click vào <strong className="text-slate-800 font-medium">biểu tượng tia sét</strong> ở trong bảng để bắt đầu tiến trình dịch (vẫn giữ nguyên định dạng bản gốc).</li>
                                                    </ol>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <p>No production files yet.</p>
                                                <p className="text-xs">Upload VI files to start translating.</p>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            files.map((file, fileIdx) => {
                                const hasTranslation = file.translationStatus === 'done' && Array.isArray(file.aiOutput) && file.aiOutput.length > 0;
                                const isFirstFile = fileIdx === 0;
                                return (
                                <tr key={file.id} className="hover:bg-slate-50 transition-colors group">
                                    <td className="px-6 py-4">
                                        <div className="flex items-start gap-3">
                                            {(() => {
                                                const isBusy = file.translationStatus === 'running' || file.auditStatus === 'running';
                                                return (
                                                    <button
                                                        data-tour={isFirstFile ? 'file-view-btn' : undefined}
                                                        onClick={() => !isBusy && onSelectFile(file.id)}
                                                        disabled={isBusy}
                                                        className={`mt-0.5 p-1.5 rounded border transition-colors bg-slate-50 border-slate-200 ${
                                                            isBusy
                                                                ? 'text-slate-300 cursor-not-allowed opacity-60'
                                                                : 'text-slate-400 hover:text-indigo-600 hover:bg-indigo-50'
                                                        }`}
                                                        title={isBusy ? 'Đang dịch — chờ xong rồi xem' : (hasTranslation ? 'Xem chi tiết' : 'Xem file (chưa dịch — debug glossary)')}
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                );
                                            })()}
                                            <div>
                                                <div className="font-semibold text-slate-800 text-sm">{file.fileName}</div>
                                                <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-2">
                                                    <span>Thêm {new Date(file.createdAt).toLocaleDateString()}</span>
                                                    {(() => {
                                                        const ruleCount = getRulesForFile(file).length;
                                                        const isLive = !file.matchedRules;
                                                        const hasGlossary = synonymGroups.length > 0;

                                                        // Project chưa có thuật ngữ nào → ẩn (UX minimal noise)
                                                        if (!hasGlossary) return null;

                                                        // Có glossary nhưng 0 match → muted gray badge
                                                        if (ruleCount === 0) {
                                                            return (
                                                                <span
                                                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-[10px] font-medium text-slate-400"
                                                                    title={`Project có ${synonymGroups.length} thuật ngữ nhưng file này không khớp với từ nào`}
                                                                >
                                                                    <BookMarked className="w-2.5 h-2.5" />
                                                                    0 thuật ngữ tùy chỉnh
                                                                </span>
                                                            );
                                                        }

                                                        // Có match → emerald clickable
                                                        return (
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); setRulesModalFileId(file.id); }}
                                                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-[10px] font-bold text-emerald-700 transition-colors"
                                                                title={isLive ? `Xem ${ruleCount} thuật ngữ sẽ áp dụng khi dịch` : 'Xem thuật ngữ đã khớp'}
                                                            >
                                                                <BookMarked className="w-2.5 h-2.5" />
                                                                {ruleCount} thuật ngữ tùy chỉnh
                                                            </button>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-center" data-tour={isFirstFile ? 'file-credit' : undefined}>
                                        <TokenBadge
                                            compact
                                            credits={fileCredits(file)}
                                            accurateStatus={file.estimatedCredits ? 'ok' : 'idle'}
                                        />
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col items-center gap-1">
                                            {(() => {
                                                const status = file.translationStatus;
                                                // Running with live progress
                                                if (status === 'running') {
                                                    // Single running state — luôn hiện X/Y, đọc từ file.translateProgress (per-batch update).
                                                    // Fallback total từ backendSegments (LIGHT field, trong state.json) → robust kể cả
                                                    // khi translateProgress race-lost. sourceText là HEAVY, undefined sau refresh.
                                                    const tp = file.translateProgress;
                                                    const completed = tp?.completedSegments ?? 0;
                                                    const total = tp?.totalSegments
                                                        ?? file.backendSegments?.length
                                                        ?? (file.sourceText?.split('\n').filter((s: string) => s.trim()).length || 0);
                                                    return (
                                                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold rounded-lg bg-amber-50 text-amber-700 border border-amber-200">
                                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                            <span className="tabular-nums">{completed}/{total}</span>
                                                        </div>
                                                    );
                                                }
                                                if (status === 'done') {
                                                    return (
                                                        <div className="flex flex-col items-center gap-0.5">
                                                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                                                            <button
                                                                onClick={() => onRunFile(file.id)}
                                                                className="text-[10px] text-slate-400 hover:text-emerald-600 underline underline-offset-2 decoration-dotted"
                                                                title="Dịch lại"
                                                            >
                                                                Dịch lại
                                                            </button>
                                                        </div>
                                                    );
                                                }
                                                if (status === 'error') {
                                                    return (
                                                        <button
                                                            onClick={() => onRunFile(file.id)}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg text-amber-700 bg-amber-50 border border-amber-300 hover:bg-amber-100 transition-colors"
                                                            title={file.error || 'Thử lại'}
                                                        >
                                                            <RefreshCw className="w-3.5 h-3.5" />
                                                            Thử lại
                                                        </button>
                                                    );
                                                }
                                                // pending + đã click → đang chờ insertJobs response (~100-500ms)
                                                if (status === 'pending' && activeTranslatingFileId === file.id) {
                                                    return (
                                                        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-slate-100 text-slate-600 border border-slate-200 whitespace-nowrap">
                                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                            Khởi tạo
                                                        </div>
                                                    );
                                                }
                                                // pending → primary CTA
                                                return (
                                                    <button
                                                        onClick={() => onRunFile(file.id)}
                                                        data-tour={isFirstFile ? 'file-translate-btn' : undefined}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 transition-colors shadow-sm shadow-emerald-200"
                                                        title="Dịch tài liệu"
                                                    >
                                                        <Zap className="w-3.5 h-3.5 fill-current" />
                                                        Dịch
                                                    </button>
                                                );
                                            })()}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {/* 4 states (priority):
                                             1. auditStatus='done' → show score
                                             2. auditStatus='running' → spinner
                                             3. translationStatus='done' + no audit yet → "Chạy QC" action button
                                             4. Pre-translate → skipQC toggle (same as before) */}
                                        {(() => {
                                            // QC done → score badge (clickable to re-run)
                                            // US-19-16: auditResult lazy-load. Fallback auditScore (lightweight, in state.json)
                                            // để table show điểm ngay sau reload, không cần mở file detail trigger ensureFileContentLoaded.
                                            if (file.auditStatus === 'done' && (file.auditResult || typeof file.auditScore === 'number')) {
                                                const score = file.auditResult?.overallScore ?? file.auditScore ?? 0;
                                                const tone = score >= 8 ? 'emerald' : score >= 6 ? 'amber' : 'red';
                                                const cls = {
                                                    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
                                                    amber: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
                                                    red: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
                                                }[tone];
                                                return (
                                                    <button
                                                        onClick={() => onRunQC?.(file.id)}
                                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-black tabular-nums rounded-lg border transition-colors ${cls}`}
                                                        title="Click để chạy QC lại"
                                                    >
                                                        <CheckCircle className="w-3.5 h-3.5" />
                                                        {score}/10
                                                    </button>
                                                );
                                            }
                                            // QC running → spinner badge
                                            if (file.auditStatus === 'running') {
                                                return (
                                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold rounded-lg bg-violet-50 text-violet-700 border border-violet-200">
                                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                        Đang kiểm tra
                                                    </div>
                                                );
                                            }
                                            // QC error → retry button
                                            if (file.auditStatus === 'error') {
                                                return (
                                                    <button
                                                        onClick={() => onRunQC?.(file.id)}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg text-red-700 bg-red-50 border border-red-300 hover:bg-red-100 transition-colors"
                                                        title="QC lỗi — thử lại"
                                                    >
                                                        <RefreshCw className="w-3.5 h-3.5" />
                                                        Thử lại
                                                    </button>
                                                );
                                            }
                                            // Translation done, chưa QC → primary "Chạy QC" button (action)
                                            if (file.translationStatus === 'done' && onRunQC) {
                                                return (
                                                    <button
                                                        onClick={() => onRunQC(file.id)}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg text-white bg-violet-600 hover:bg-violet-700 transition-colors shadow-sm shadow-violet-200"
                                                        title="Chạy Quality Check (đánh giá bản dịch)"
                                                    >
                                                        <ShieldCheck className="w-3.5 h-3.5" />
                                                        Chạy QC
                                                    </button>
                                                );
                                            }
                                            // Pre-translate → per-file QC toggle. Khi tick → run QC ngay sau khi dịch xong.
                                            // Lock toggle khi đang dịch (running) hoặc đang khởi tạo (pending + activeId match)
                                            // — tránh user đổi ý mid-flight, settings phải fix lúc gọi API.
                                            const qcEnabled = !file.skipQC;
                                            const isLocked = file.translationStatus === 'running'
                                                || (file.translationStatus === 'pending' && activeTranslatingFileId === file.id);
                                            return (
                                                <label
                                                    data-tour={isFirstFile ? 'file-qc-toggle' : undefined}
                                                    className={`inline-flex items-center gap-2 text-sm select-none ${isLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                    title={isLocked ? 'Đang dịch — không thể đổi cài đặt QC' : undefined}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={qcEnabled}
                                                        disabled={isLocked}
                                                        onChange={(e) => onToggleFileQC?.(file.id, !e.target.checked)}
                                                        className={`w-4 h-4 accent-violet-600 ${isLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                                        title={isLocked ? 'Đang dịch — không đổi được' : 'Bật để chạy Kiểm tra Chất lượng (QC) sau khi dịch'}
                                                    />
                                                    <span className={qcEnabled ? "text-violet-700 font-semibold" : "text-slate-400"}>
                                                        Kiểm tra
                                                    </span>
                                                </label>
                                            );
                                        })()}
                                    </td>
                                    <td className="px-2 py-4 text-right">
                                        <button
                                            onClick={() => onDeleteFile(file.id)}
                                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Xóa file"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal: Matched terminology rules */}
            {rulesModalFile && (
                <div
                    className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
                    onClick={() => setRulesModalFileId(null)}
                >
                    <div
                        className="bg-white rounded-xl shadow-2xl w-[640px] max-w-full max-h-[80vh] flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                            <div>
                                <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                                    <BookMarked className="w-5 h-5 text-emerald-600" />
                                    Thuật ngữ đã khớp
                                </h3>
                                <p className="text-xs text-slate-500 mt-0.5 truncate max-w-md">{rulesModalFile.fileName}</p>
                            </div>
                            <button
                                onClick={() => setRulesModalFileId(null)}
                                className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4">
                            {rulesModalRules.length === 0 ? (
                                <div className="text-center py-12 text-slate-400 italic text-sm">
                                    Chưa có thuật ngữ nào được khớp.
                                </div>
                            ) : (
                                <div className="space-y-1.5">
                                    {rulesModalRules.map((rule, idx) => (
                                        <div key={idx} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${idx % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
                                            <span className="text-[10px] font-mono text-slate-400 w-6 shrink-0 tabular-nums">{idx + 1}</span>
                                            <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs font-medium border border-indigo-100">{rule.source}</span>
                                            <ArrowRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                                            <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium border border-amber-200">{rule.target}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                            <span className="text-xs text-slate-500">Tổng cộng: <span className="font-bold text-slate-700">{rulesModalRules.length}</span> thuật ngữ</span>
                            <button
                                onClick={() => setRulesModalFileId(null)}
                                className="px-4 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                            >
                                Đóng
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TranslateTab;
